import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Pencil, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LoginForm = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand-50 flex items-center justify-center px-6">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm space-y-6">
        <div>
          <p className="font-serif italic text-3xl tracking-widest text-sand-900">Frank en Vrij</p>
          <p className="mt-2 font-sans text-xs font-light tracking-[0.3em] uppercase text-terracotta-500">Studio — inloggen</p>
        </div>
        <Input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="E-mailadres"
          data-testid="login-email-input"
          className="bg-transparent"
        />
        <Input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Wachtwoord"
          data-testid="login-password-input"
          className="bg-transparent"
        />
        {error && <p data-testid="login-error" className="font-sans text-sm text-destructive">{error}</p>}
        <Button type="submit" disabled={busy} data-testid="login-submit-button" className="w-full rounded-full">
          {busy ? "Even geduld…" : "Inloggen met e-mail"}
        </Button>
        <div className="flex items-center gap-4">
          <span className="h-px flex-1 bg-sand-200" />
          <span className="font-sans text-xs font-light text-sand-900/40">of</span>
          <span className="h-px flex-1 bg-sand-200" />
        </div>
        <Button
          type="button"
          variant="outline"
          data-testid="login-google-button"
          className="w-full rounded-full bg-transparent"
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + "/admin";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
        >
          Inloggen met Google
        </Button>
      </form>
    </div>
  );
};

const TracksTab = () => {
  const [tracks, setTracks] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/tracks").then((r) => setTracks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !title.trim()) {
      toast.error("Kies een audiobestand en vul een titel in");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("description", description);
      fd.append("file", file);
      await api.post("/tracks", fd);
      toast.success("Track geüpload");
      setTitle("");
      setDescription("");
      setFile(null);
      e.target.reset();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/tracks/${id}`);
      toast.success("Track verwijderd");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-10">
      <form onSubmit={upload} data-testid="track-upload-form" className="space-y-4 border border-sand-200 rounded-lg p-6 bg-sand-50">
        <h3 className="font-serif text-2xl font-light text-sand-900">Nieuwe track uploaden</h3>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titel" data-testid="track-title-input" className="bg-transparent" />
        <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Korte beschrijving (optioneel)" data-testid="track-description-input" className="bg-transparent" />
        <input
          type="file"
          accept="audio/*"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid="track-file-input"
          className="block w-full font-sans text-sm font-light text-sand-900/70 file:mr-4 file:rounded-full file:border-0 file:bg-terracotta-500 file:px-5 file:py-2 file:text-sand-50 file:text-xs file:tracking-[0.15em] file:uppercase hover:file:bg-terracotta-600"
        />
        <Button type="submit" disabled={busy} data-testid="track-upload-button" className="rounded-full">
          {busy ? "Uploaden…" : "Uploaden"}
        </Button>
      </form>

      <ul className="divide-y divide-sand-200" data-testid="admin-track-list">
        {tracks.length === 0 && (
          <li className="py-6 font-sans text-sm font-light text-sand-900/50">Nog geen tracks geüpload.</li>
        )}
        {tracks.map((t, i) => (
          <li key={t.id} className="py-4 flex items-center gap-4">
            <span className="flex-1 min-w-0">
              <span className="block font-serif text-xl font-light text-sand-900 truncate">{t.title}</span>
              <span className="block font-sans text-xs font-light text-sand-900/50">{t.original_filename}</span>
            </span>
            <button type="button" onClick={() => remove(t.id)} data-testid={`track-delete-button-${i}`} className="flex items-center gap-2 shrink-0 font-sans text-xs font-light tracking-[0.15em] uppercase text-sand-900/60 hover:text-destructive border border-sand-300 hover:border-destructive rounded-full px-4 py-2 transition-colors">
              <Trash2 size={14} /> Verwijder
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const StoriesTab = () => {
  const [stories, setStories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/stories").then((r) => setStories(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reset = () => {
    setEditing(null);
    setTitle("");
    setExcerpt("");
    setContent("");
  };

  const startEdit = async (story) => {
    const { data } = await api.get(`/stories/${story.slug}`);
    setEditing(data.id);
    setTitle(data.title);
    setExcerpt(data.excerpt || "");
    setContent(data.content);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      toast.error("Vul een titel en tekst in");
      return;
    }
    setBusy(true);
    try {
      if (editing) await api.put(`/admin/stories/${editing}`, { title, excerpt, content });
      else await api.post("/admin/stories", { title, excerpt, content });
      toast.success(editing ? "Verhaal bijgewerkt" : "Verhaal opgeslagen");
      reset();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/stories/${id}`);
      toast.success("Verhaal verwijderd");
      if (editing === id) reset();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-10">
      <form onSubmit={save} data-testid="story-editor-form" className="space-y-4 border border-sand-200 rounded-lg p-6 bg-sand-50">
        <h3 className="font-serif text-2xl font-light text-sand-900">{editing ? "Verhaal bewerken" : "Nieuw verhaal schrijven"}</h3>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titel" data-testid="story-title-input" className="bg-transparent" />
        <Input value={excerpt} onChange={(e) => setExcerpt(e.target.value)} placeholder="Korte samenvatting (optioneel)" data-testid="story-excerpt-input" className="bg-transparent" />
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={"Schrijf hier je verhaal…\n\nEen lege regel begint een nieuwe alinea."}
          rows={12}
          data-testid="story-content-input"
          className="bg-transparent font-serif text-lg font-light leading-relaxed"
        />
        <div className="flex gap-3">
          <Button type="submit" disabled={busy} data-testid="story-save-button" className="rounded-full">
            {busy ? "Opslaan…" : editing ? "Bijwerken" : "Publiceren"}
          </Button>
          {editing && (
            <Button type="button" variant="outline" onClick={reset} data-testid="story-cancel-button" className="rounded-full">
              Annuleren
            </Button>
          )}
        </div>
      </form>

      <ul className="divide-y divide-sand-200" data-testid="admin-story-list">
        {stories.map((s, i) => (
          <li key={s.id} className="py-4 flex items-center gap-4">
            <span className="flex-1 min-w-0">
              <span className="block font-serif text-xl font-light text-sand-900 truncate">{s.title}</span>
              {s.excerpt && <span className="block font-sans text-xs font-light text-sand-900/50 truncate">{s.excerpt}</span>}
            </span>
            <button type="button" onClick={() => startEdit(s)} data-testid={`story-edit-button-${i}`} className="text-sand-900/40 hover:text-terracotta-500 transition-colors">
              <Pencil size={17} />
            </button>
            <button type="button" onClick={() => remove(s.id)} data-testid={`story-delete-button-${i}`} className="text-sand-900/40 hover:text-destructive transition-colors">
              <Trash2 size={17} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const MessagesTab = () => {
  const [messages, setMessages] = useState([]);
  useEffect(() => {
    api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => {});
  }, []);

  return (
    <ul className="space-y-4" data-testid="admin-message-list">
      {messages.length === 0 && (
        <li className="py-6 font-sans text-sm font-light text-sand-900/50">Nog geen berichten ontvangen.</li>
      )}
      {messages.map((m) => (
        <li key={m.id} className="border border-sand-200 rounded-lg p-5 bg-sand-50">
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <p className="font-serif text-xl font-light text-sand-900">{m.name}</p>
            <p className="font-sans text-xs font-light text-sand-900/50">
              {m.email} · {new Date(m.created_at).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>
          <p className="mt-3 font-sans text-sm font-light leading-relaxed text-sand-900/75 whitespace-pre-wrap">{m.message}</p>
        </li>
      ))}
    </ul>
  );
};

export default function Admin() {
  const { user, logout } = useAuth();

  if (user === null) {
    return <div className="min-h-screen bg-sand-50 flex items-center justify-center font-sans text-sm font-light text-sand-900/50">Laden…</div>;
  }
  if (!user) return <LoginForm />;

  return (
    <div className="min-h-screen bg-sand-50 text-sand-900" data-testid="admin-dashboard">
      <header className="border-b border-sand-200">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <p className="font-serif italic text-2xl tracking-widest">Frank en Vrij <span className="not-italic font-sans text-xs tracking-[0.3em] uppercase text-terracotta-500 ml-3">Studio</span></p>
          <button type="button" onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 font-sans text-xs font-light tracking-[0.2em] uppercase text-sand-900/60 hover:text-terracotta-500 transition-colors">
            <LogOut size={14} /> Uitloggen
          </button>
        </div>
      </header>
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Tabs defaultValue="muziek">
          <TabsList className="bg-sand-100">
            <TabsTrigger value="muziek" data-testid="tab-muziek">Muziek</TabsTrigger>
            <TabsTrigger value="verhalen" data-testid="tab-verhalen">Verhalen</TabsTrigger>
            <TabsTrigger value="berichten" data-testid="tab-berichten">Berichten</TabsTrigger>
          </TabsList>
          <TabsContent value="muziek" className="pt-8"><TracksTab /></TabsContent>
          <TabsContent value="verhalen" className="pt-8"><StoriesTab /></TabsContent>
          <TabsContent value="berichten" className="pt-8"><MessagesTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
